import React from "react";

import { useAuthDetails } from "@/context/auth-context";
import { useCurrentUserGroupRole } from "@/hooks/use-current-user-group-role";
import { GroupRole, SystemRole } from "@/lib/consts";
import { Gate } from "./Gate";

type RoleGateProps = {
  children: React.ReactElement;
  debug?: boolean;
} & (
  | {
      systemRoles: SystemRole | SystemRole[];
      groupRoles?: GroupRole | GroupRole[];
    }
  | {
      systemRoles?: SystemRole | SystemRole[];
      groupRoles: GroupRole | GroupRole[];
    }
);

/**
 * Creates a Role overlay on the child component.
 *
 * When the RoleGate is hovered, it prints details in the console.
 *
 * Gate visibility:
 * - overlay is visible by super-admins only
 * - enabled by default in dev environment
 * - disabled by default in test and production environments
 * - can be enabled and disabled in the /admin/my-settings page
 *
 * Setting visibility depends on the `setting` value:
 * - 🟩 Green overlay - the setting is enabled and will be visible without the overlay
 * - 🟥 Red overlay - the setting is disabled and will be hidden without the overlay
 *
 * Can only wrap one child component.
 * Either `systemRoles` or `groupRoles` can be provided. If both are provided,
 * the user must satisfy at least one.
 *
 * Usage:
 * ```tsx
 * <RoleGate systemRoles="super">
 *   <SuperAdminComponent />
 * </RoleGate>
 *
 * <RoleGate groupRoles={["admin", "creator"]}>
 *   <GroupAdminComponent />
 * </RoleGate>
 * ```
 */
// ts-unused-exports:disable-next-line
export const RoleGate: React.FC<RoleGateProps> = ({
  systemRoles,
  groupRoles,
  children,
  debug,
}) => {
  const authDetails = useAuthDetails();
  const groupRole = useCurrentUserGroupRole();

  if (!authDetails) {
    return null;
  }

  const systemRolesArray = systemRoles
    ? Array.isArray(systemRoles)
      ? systemRoles
      : [systemRoles]
    : [];
  const groupRolesArray = groupRoles
    ? Array.isArray(groupRoles)
      ? groupRoles
      : [groupRoles]
    : [];

  const systemAllowed =
    systemRolesArray.length > 0 &&
    systemRolesArray.includes(authDetails.role as SystemRole);
  const groupAllowed =
    groupRolesArray.length > 0 &&
    groupRolesArray.includes(groupRole as GroupRole);
  const allowed = systemAllowed || groupAllowed;

  const allRoles = [...systemRolesArray, ...groupRolesArray];

  if (!allowed && !debug) {
    return null;
  }

  return (
    <Gate
      name="RoleGate"
      description={`Gate accessible by: ${allRoles.join(", ")}.`}
      value={allowed ? allRoles[0] : undefined}
      equals={allRoles}
      debug={debug}
    >
      {children}
    </Gate>
  );
};
